import './index.css';
import { jsx } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { ChevronRightOutlinedSize24, ChevronLeftOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { s as styles, N as NumberPagesAction } from './NumberPagesAction-D38RFCLU.js';
import { usePages, PageControls } from './usePages.js';
import '@hh.ru/magritte-ui-typography';

const baseRenderItem = ({ isPrevious, isNext, ...props }) => {
    return jsx(NumberPagesAction, { ...props, isIcon: isPrevious || isNext });
};
const getPropsByScreenSize = (isXS, isS) => {
    if (isXS) {
        return [0, 0];
    }
    else if (isS) {
        return [1, 1];
    }
    return [2, 1];
};
const NumberPages = ({ first, last, current, onChange, 'aria-label': ariaLabel, renderItem = baseRenderItem, renderNavigation = baseRenderItem, getAriaLabelItem, ...props }) => {
    const { isXS, isS } = useBreakpoint();
    const [initialSiblingCount, initialBoundaryCount] = getPropsByScreenSize(isXS, isS);
    const [siblingCount, setSiblingCount] = useState(initialSiblingCount);
    const [boundaryCount, setBoundaryCount] = useState(initialBoundaryCount);
    const pages = usePages({ first, last, current, siblingCount, boundaryCount });
    useEffect(() => {
        const [siblingCount, boundaryCount] = getPropsByScreenSize(isXS, isS);
        setSiblingCount(siblingCount);
        setBoundaryCount(boundaryCount);
    }, [isXS, isS]);
    if (first > last) {
        throw new Error(`Magritte component NumberPages: First prop more than last prop. first: ${first}, last: ${last}`);
    }
    if (current < first || current > last) {
        throw new Error(`Magritte component NumberPages: Current is wrong. current: ${current}, first: ${first}, last: ${last}`);
    }
    if (last - first < 1) {
        return null;
    }
    const renderPageItem = (item) => {
        const isSelected = item === current;
        const baseProps = {
            selected: isSelected,
            key: item,
            'aria-label': getAriaLabelItem?.(item),
        };
        switch (item) {
            case PageControls.Previous:
                return renderNavigation({
                    ...baseProps,
                    'data-qa': 'number-pages-previous',
                    onClick: () => {
                        onChange?.(current - 1);
                    },
                    isPrevious: true,
                    page: current - 1,
                    children: jsx(ChevronLeftOutlinedSize24, {}),
                });
            case PageControls.StartEllipsis:
            case PageControls.EndEllipsis:
                return (jsx("li", { "data-qa": "number-pages-ellipsis", className: styles.numberPagesEllipsis, children: "..." }, item));
            case PageControls.Next:
                return renderNavigation({
                    ...baseProps,
                    'data-qa': 'number-pages-next',
                    onClick: () => {
                        onChange?.(current + 1);
                    },
                    isNext: true,
                    page: current + 1,
                    children: jsx(ChevronRightOutlinedSize24, {}),
                });
            default:
                return renderItem({
                    ...baseProps,
                    onClick: () => {
                        if (!isSelected) {
                            onChange?.(item);
                        }
                    },
                    'data-qa': `number-pages-${item} ${isSelected ? 'number-pages-selected' : ''}`,
                    page: item,
                    selected: isSelected,
                    children: item,
                });
        }
    };
    return (jsx("nav", { ...props, "aria-label": ariaLabel, className: classnames(styles.numberPagesWrapper, {
            [styles.numberPagesWrapperWithoutNavigationButtons]: !pages.includes(PageControls.Next) && !pages.includes(PageControls.Previous),
        }), children: jsx("ul", { className: styles.numberPagesContainer, children: pages.map(renderPageItem) }) }));
};

export { NumberPages };
//# sourceMappingURL=NumberPages.js.map
